// frontend/src/lib/permissions.ts
import { Collections, type DoctorBranchRolesRoleOptions, SubscriptionsStatusOptions } from "@/types/pocketbase";

// ─── Types ───

type BranchWithOwner = {
	id: string;
	doctor?: { id: string };
};

export type BranchOwnerInfo = {
	ownerDoctorId: string | undefined;
	isOwner: boolean;
	isGuest: boolean;
};

export type SubscriptionStatusInfo = {
	status: SubscriptionsStatusOptions | undefined;
	trialEndsAt: string | undefined;
	endsAt: string | undefined;
	isActive: boolean;
	isBlocked: boolean;
	canPerformActions: boolean;
};

export type PlanLimits = {
	maxBranches: number;
	maxDoctors: number;
	maxPatients: number;
	maxStorageMB: number;
	maxAppointmentsPerMonth: number;
	maxProcedureTypes: number;
	maxFormularies: number;
	planName: string;
};

// ─── Branch Owner Resolution ───

export function resolveBranchOwner({
	branches,
	activeBranchId,
	sessionDoctorId,
}: {
	branches: Array<BranchWithOwner> | undefined;
	activeBranchId: string;
	sessionDoctorId: string | undefined;
}): BranchOwnerInfo {
	const activeBranch = branches?.find((b) => b.id === activeBranchId);
	const ownerDoctorId = activeBranch?.doctor?.id;
	const isOwner = !!ownerDoctorId && ownerDoctorId === sessionDoctorId;
	const isGuest = !!ownerDoctorId && !isOwner;

	return { ownerDoctorId, isOwner, isGuest };
}

export function resolveSubscriptionDoctorId({
	ownerDoctorId,
	isGuest,
	sessionDoctorId,
}: {
	ownerDoctorId: string | undefined;
	isGuest: boolean;
	sessionDoctorId: string | undefined;
}): string | undefined {
	return isGuest ? ownerDoctorId : sessionDoctorId;
}

// ─── Subscription Status ───

function hasDatePassed({ date }: { date?: string }): boolean {
	if (!date) return false;
	return new Date(date) < new Date();
}

export function isActiveSubscription({
	status,
	trialEndsAt,
	endsAt,
}: {
	status?: SubscriptionsStatusOptions;
	trialEndsAt?: string;
	endsAt?: string;
}): boolean {
	if (status === SubscriptionsStatusOptions.active) {
		const effectiveDate = endsAt || trialEndsAt;
		if (!effectiveDate) return true;
		return !hasDatePassed({ date: effectiveDate });
	}
	if (status === SubscriptionsStatusOptions.on_trial) {
		if (!trialEndsAt) return false;
		return !hasDatePassed({ date: trialEndsAt });
	}
	return false;
}

export function getSubscriptionStatus({
	status,
	trialEndsAt,
	endsAt,
}: {
	status?: SubscriptionsStatusOptions;
	trialEndsAt?: string;
	endsAt?: string;
}): SubscriptionStatusInfo {
	const isActive = isActiveSubscription({ status, trialEndsAt, endsAt });

	return {
		status,
		trialEndsAt,
		endsAt,
		isActive,
		isBlocked: !isActive,
		canPerformActions: isActive,
	};
}

// ─── Role-Based Permissions ───

type CRUD = {
	create: boolean;
	edit: boolean;
	delete: boolean;
	view: boolean;
};

type SubscriptionsActions = {
	upgrade: boolean;
};

type DoctorsActions = {
	invite: boolean;
	remove: boolean;
	edit: boolean;
};

export type Action = keyof CollectionActions[keyof CollectionActions];

export type CollectionActions = {
	[Collections.Branches]: CRUD;
	[Collections.Patients]: CRUD;
	[Collections.Subscriptions]: SubscriptionsActions;
	[Collections.Doctors]: DoctorsActions;
};

type PermissionMatrix = {
	[R in DoctorBranchRolesRoleOptions]: {
		[K in keyof CollectionActions]: CollectionActions[K];
	};
};

const allowCrud: CRUD = {
	create: true,
	edit: true,
	delete: true,
	view: true,
};

const permissionMatrix: PermissionMatrix = {
	owner: {
		doctors: { edit: true, remove: true, invite: true },
		patients: { ...allowCrud },
		branches: { ...allowCrud },
		subscriptions: { upgrade: true },
	},
	guest: {
		doctors: { edit: false, remove: false, invite: false },
		patients: { ...allowCrud },
		branches: { create: false, edit: false, view: true, delete: false },
		subscriptions: { upgrade: false },
	},
};

export const hasPermission = <T extends keyof CollectionActions>({
	role,
	collection,
	action,
}: {
	role: DoctorBranchRolesRoleOptions;
	collection: T;
	action: keyof CollectionActions[T];
}) => {
	if (!role) return false;
	return permissionMatrix[role]?.[collection]?.[action] ?? false;
};
